/* @jsxRuntime automatic */
/* @jsxImportSource @superweb/css */

import {
  Suspense,
  useEffect,
  useLayoutEffect,
  useRef,
  type ReactElement,
} from "react";
import {
  createBrowserRouter,
  Route,
  RouterProvider,
  Routes,
  useLocation,
} from "react-router-dom";
import { Helmet, HelmetProvider } from "react-helmet-async";

import { HttpError, QueryClient, QueryClientProvider } from "@superweb/api";
import { cssFns, CssInstance, useCss } from "@superweb/css";
import {
  AppContainer,
  Messages as SuperwebUiMessages,
  Theme,
  useBrandColors,
  useUiColors,
} from "@superweb/ui";
import { useLocale } from "@superweb/intl";

import { useV1ParkInfoGet } from "@fleet/api/fleet-forms";
import {
  ErrorBoundary,
  Messages as FleetUiMessages,
  Unexpected,
  YangoTheme,
} from "@fleet/ui";

import {
  Navigate,
  searchParamTypes as t,
  useSearchParams,
} from "#internal/router-new";

import { Form } from "./form";
import { Flags } from "./flags";
import { Messages } from "./intl";
import { AppLocale } from "./app-locale";
import { Errors } from "./errors";
import { NotFound, OperationId, useOperationIdContext } from "./ui";
import { useLogHit } from "./analytics/logActions";
import { initGtag } from "./analytics/gtag";
import { initFBQ } from "./analytics/fbq";
import { initTTQ } from "./analytics/ttq";
import "./analytics/initYM";
import faviconYango from "./images/favicon-yango.png";
import faviconYandex from "./images/favicon-yandex.png";
import { ArchivePage } from "./form/archive";
import { PageSpinner } from "./ui";
import { authWithPassport } from "./auth";
import { ColorSchemeProvider } from "./ui/color-scheme-provider";
import { RegisterForm } from "./form/register";
import { VehicleForm } from "./form/vehicle";
import { DetailsForm } from "./form/details";
import { EmploymentTypeForm } from "./form/employment-type";
import { ProfessionForm } from "./form/profession";
import { DownloadAppPage } from "./form/download-app-page";
import { LicenseForm } from "./form/license";
import { useReportWebVitals } from "./analytics";

const queryClient = new QueryClient();

const useLogLocation = (logHit: (url: string) => void) => {
  const location = useLocation();
  const prevPathname = useRef(location.pathname);

  useEffect(() => {
    if (location.pathname !== prevPathname.current) {
      prevPathname.current = location.pathname;
      logHit(`${location.pathname}${location.search}`);
    }
  }, [location.pathname, location.search, logHit]);
};

const AppContainerWrapper = ({ children }: { children: ReactElement }) => {
  const css = useCss();
  const brandColors = useBrandColors();
  const locale = useLocale();

  const bodyClassName = css({
    color: brandColors.text,
    backgroundColor: brandColors.background,
    ...cssFns.margin("0"),
  });

  useLayoutEffect(() => {
    const list = bodyClassName.split(" ");
    document.body.classList.add(...list);
    return () => {
      document.body.classList.remove(...list);
    };
  }, [bodyClassName]);

  useLayoutEffect(() => {
    document.body.dir = locale.textInfo.direction;
  }, [locale.textInfo.direction]);

  return <AppContainer>{children}</AppContainer>;
};

const Root = () => {
  return (
    <AppContainerWrapper>
      <OperationId>
        <ErrorBoundary
          fallback={({ error, traceId, clientDebugId, reset }) => {
            if (error instanceof HttpError && error.status === 403) {
              authWithPassport();

              return <PageSpinner />;
            }

            return (
              <div
                css={{ height: "100vh", display: "flex", alignItems: "center" }}
              >
                <Unexpected
                  traceId={traceId}
                  clientDebugId={clientDebugId}
                  reset={reset}
                />
              </div>
            );
          }}
        >
          <Suspense fallback={<PageSpinner />}>
            <SuperwebUiMessages>
              <FleetUiMessages>
                <Messages>
                  <Errors>
                    <AppContent />
                  </Errors>
                </Messages>
              </FleetUiMessages>
            </SuperwebUiMessages>
          </Suspense>
        </ErrorBoundary>
      </OperationId>
    </AppContainerWrapper>
  );
};

const router = createBrowserRouter([{ path: "*", Component: Root }]);

export const App = () => {
  const yangoBrand = window.location.hostname.split(".").includes("yango");

  return (
    <Flags>
      <CssInstance
        nonce={
          (
            document.querySelector(
              "meta[property=csp-nonce]",
            ) as HTMLMetaElement | null
          )?.nonce
        }
      >
        <QueryClientProvider client={queryClient}>
          <AppLocale>
            <HelmetProvider>
              <ColorSchemeProvider>
                <Theme value={yangoBrand ? YangoTheme : undefined}>
                  <RouterProvider router={router} />
                </Theme>
              </ColorSchemeProvider>
            </HelmetProvider>
          </AppLocale>
        </QueryClientProvider>
      </CssInstance>
    </Flags>
  );
};

const AppContent = () => {
  const uiColors = useUiColors();
  const locale = useLocale();
  const logHit = useLogHit();
  const location = useLocation();

  useLogLocation(logHit);
  useReportWebVitals();

  const [{ ref_id, specification }] = useSearchParams({
    ref_id: t.string,
    specification: t.string,
  });

  const isIframe = window.self !== window.top;
  const isDelivery = specification === "delivery";

  const { sessionId } = useOperationIdContext();

  const { data: parkInfo } = useV1ParkInfoGet(
    {
      headers: {
        language: locale.toString(),
        "x-session-id": sessionId,
      },
      query: { ref_id: ref_id! },
    },
    {
      enabled: Boolean(ref_id),
    },
  );

  useEffect(() => {
    if (parkInfo?.status === 200) {
      switch (parkInfo.data.channel_id) {
        case "google_ads": {
          const ga_id = parkInfo.data.google_adwords_id;
          initGtag(ga_id);
          break;
        }
        case "facebook": {
          const pixel_id = parkInfo.data.fb_pixel_id;
          initFBQ(pixel_id);
          break;
        }
        case "tik_tok": {
          const tiktok_pixel_id = parkInfo.data.tiktok_pixel_id;
          initTTQ(tiktok_pixel_id);
          break;
        }
        default:
          break;
      }
    }
  }, [parkInfo]);

  if (!ref_id || !parkInfo || parkInfo.status === 400) return <NotFound />;

  return (
    <>
      <Helmet>
        <link
          rel="icon"
          type="image/png"
          href={parkInfo.data.brand === "yango" ? faviconYango : faviconYandex}
          sizes="16x16"
        />
      </Helmet>
      <ColorSchemeProvider>
        <Theme value={parkInfo.data.brand === "yango" ? YangoTheme : undefined}>
          <div
            css={{
              height: "100vh",
              display: "flex",
              minWidth: "min-content",
              isolation: "isolate",
              ...(isIframe && {
                maxHeight: "600px",
                maxWidth: "600px",
              }),
              justifyContent: "center",
            }}
          >
            <div
              css={{
                flexGrow: "1",
                display: "grid",
                backgroundColor: uiColors.background,
              }}
            >
              <Suspense fallback={<PageSpinner />}>
                <Routes>
                  {parkInfo.status !== 200 ? (
                    <Route
                      path="*"
                      element={
                        <ArchivePage
                          parkInfo={
                            parkInfo.data as Extract<
                              typeof parkInfo,
                              { status: 410 }
                            >["data"]
                          }
                        />
                      }
                    />
                  ) : parkInfo.data.passport_authorization ? (
                    <>
                      <Route
                        path="/register/*"
                        element={<RegisterForm isIframe={isIframe} />}
                      />
                      <Route
                        path="/employment-type"
                        element={<EmploymentTypeForm isIframe={isIframe} />}
                      />
                      <Route
                        path="/profession/*"
                        element={<ProfessionForm isIframe={isIframe} />}
                      />
                      <Route
                        path="/license"
                        element={<LicenseForm isIframe={isIframe} />}
                      />
                      <Route
                        path="/vehicle"
                        element={<VehicleForm isIframe={isIframe} />}
                      />
                      <Route
                        path="/details"
                        element={<DetailsForm isIframe={isIframe} />}
                      />
                      <Route
                        path="/download-app"
                        element={<DownloadAppPage isIframe={isIframe} />}
                      />
                      <Route
                        path="/forms"
                        element={
                          <Navigate
                            to={`/register${location.search}`}
                            replace
                          />
                        }
                      />
                    </>
                  ) : (
                    <Route
                      path="/forms"
                      element={
                        <Form
                          parkInfo={parkInfo.data}
                          refId={ref_id}
                          isIframe={isIframe}
                          isDelivery={isDelivery}
                          country={parkInfo.data.country_code}
                        />
                      }
                    />
                  )}
                  <Route path="*" element={<NotFound />} />
                  <Route
                    path="/archive"
                    element={
                      <ArchivePage
                        parkInfo={
                          parkInfo.data as Extract<
                            typeof parkInfo,
                            { status: 410 }
                          >["data"]
                        }
                      />
                    }
                  />
                </Routes>
              </Suspense>
            </div>
          </div>
        </Theme>
      </ColorSchemeProvider>
    </>
  );
};
